/**
 * Shared trend vocabulary — one source of truth for every metric that shows
 * a direction (risk score, delivery confidence, the Portfolio Health
 * sparkline, the 30-day chart annotation).
 *
 * Three separate ideas, kept separate on purpose:
 *
 *  - DIRECTION: which way the number moved ('up' | 'down' | 'flat'), or
 *    'unknown' when we cannot say. Direction is a fact about the data.
 *  - POLARITY: whether "up" is good news for THIS metric. Risk score is
 *    lower-is-better; delivery confidence is higher-is-better. Polarity is a
 *    property of the metric, declared once by the caller.
 *  - SENTIMENT: derived from direction x polarity. Colour comes from
 *    sentiment, never from direction alone — hardcoding "rising = red" is
 *    exactly the bug this module exists to prevent.
 *
 * 'unknown' is a first-class state. A trend we cannot compute must render as
 * unknown — never as 'flat'/"Stable". Stable is a claim; unknown is an
 * admission. (See the repo's recurring absence-rendered-as-health defect.)
 *
 * The backend counterpart is risk.py's _compute_trend, which emits
 * 'worsening' | 'improving' | 'stable' | 'unknown' about the risk score.
 * Keep the ±0.3 cut and the vocabulary in sync with it.
 */

export type TrendDirection = 'up' | 'down' | 'flat' | 'unknown';
export type MetricPolarity = 'higher-is-better' | 'lower-is-better';
export type TrendSentiment = 'positive' | 'negative' | 'neutral' | 'unknown';

/** Matches the backend's trend cut (risk.py _compute_trend). */
export const TREND_DELTA_THRESHOLD = 0.3;

/**
 * Minimum series points before a direction may be claimed from a series —
 * matches the 30-day chart's "Building trend" rule in ProjectDashboard.
 */
export const MIN_TREND_POINTS = 3;

/**
 * Canonical mapping from the backend risk trend string ('worsening' means
 * the risk score is moving up). Missing, 'unknown', and unrecognised values
 * all map to 'unknown' — never to 'flat'.
 */
export function riskTrendToDirection(trend: string | null | undefined): TrendDirection {
  if (trend === 'worsening') return 'up';
  if (trend === 'improving') return 'down';
  if (trend === 'stable') return 'flat';
  return 'unknown';
}

/**
 * Direction of a metric that is a monotonic inversion of another (delivery
 * confidence is derived from the risk score, so its direction is the risk
 * direction flipped). Flat and unknown are their own inverses.
 */
export function invertDirection(direction: TrendDirection): TrendDirection {
  if (direction === 'up') return 'down';
  if (direction === 'down') return 'up';
  return direction;
}

/**
 * Direction of a score series (oldest -> newest), using the same ±0.3 cut as
 * the backend. Below minPoints the answer is 'unknown', not 'flat'.
 */
export function directionFromSeries(
  series: number[],
  minPoints: number = MIN_TREND_POINTS,
): TrendDirection {
  if (series.length < minPoints) return 'unknown';
  const delta = series[series.length - 1] - series[0];
  if (delta > TREND_DELTA_THRESHOLD) return 'up';
  if (delta < -TREND_DELTA_THRESHOLD) return 'down';
  return 'flat';
}

export function trendSentiment(
  direction: TrendDirection,
  polarity: MetricPolarity,
): TrendSentiment {
  if (direction === 'unknown') return 'unknown';
  if (direction === 'flat') return 'neutral';
  const isGood = (direction === 'up') === (polarity === 'higher-is-better');
  return isGood ? 'positive' : 'negative';
}

/**
 * Text tone per sentiment — the app's established trend palette (emerald for
 * good movement, red for bad, gray for neutral; unknown is dimmer than
 * neutral so it reads as absence, not calm).
 */
export const SENTIMENT_TEXT: Record<TrendSentiment, string> = {
  positive: 'text-emerald-600 dark:text-emerald-400',
  negative: 'text-red-600 dark:text-red-400',
  neutral: 'text-gray-500 dark:text-gray-400',
  unknown: 'text-gray-400 dark:text-gray-500',
};

/**
 * Direction words, not sentiment words: the label says which way the number
 * moved, colour and arrow say whether that is good. ("Rising" green on a
 * confidence card and "Rising" red on a risk card is the whole point.)
 */
export const DIRECTION_LABEL: Record<TrendDirection, string> = {
  up: 'Rising',
  down: 'Falling',
  flat: 'Stable',
  unknown: 'No trend yet',
};
